import { useEffect, useRef, useState } from 'react';
import { Flex, Text, Tooltip } from '@optiaxiom/react';
import {
  IconAgent,
  IconArrowsRotate,
  IconCheckCircleSolid,
  IconCodeBranch,
  IconSparkles,
} from '@optimizely/axiom-icons';

import { DirectoryAgent } from '../../../services/agent-directory';

import { styles } from './AgentCardView.css';
import { TagDisplay } from './TagDisplay';

type AgentCardViewProps = {
  agent: DirectoryAgent;
  isSelected?: boolean;
  onSelect?: () => void;
};

const AgentTypeIcon = {
  specialized: <IconAgent size="16" />,
  workflow: <IconCodeBranch size="16" />,
};

export function AgentCardView({ agent, isSelected = false, onSelect }: AgentCardViewProps) {
  const agentIcon = AgentTypeIcon[agent.agent_type] || <IconSparkles size="16" />;
  const nameRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = nameRef.current;
    if (element) {
      // Check if the text is truncated by comparing scroll height with client height
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [agent.name]);

  return (
    <Flex
      className={`${styles.cardViewContainer} ${isSelected ? styles.selected : ''}`}
      onClick={onSelect}
      style={{ cursor: onSelect ? 'pointer' : 'default' }}
    >
      {/* Name and Icon */}
      <Flex flexDirection="row" gap="8" alignItems="start">
        <Flex className={styles.iconWrapper}>
          <Flex className={styles.icon}>{agentIcon}</Flex>
          {agent.is_installed &&
            (agent.installed_version !== null &&
            agent.installed_version < agent.directory_version ? (
              <Tooltip content="New version available">
                <IconArrowsRotate className={styles.updateAvailable} />
              </Tooltip>
            ) : (
              <Tooltip content="Activated">
                <IconCheckCircleSolid className={styles.installed} />
              </Tooltip>
            ))}
        </Flex>
        <Tooltip content={agent.name} disabled={!isTruncated}>
          <Text className={styles.agentName} fontSize="md" lineClamp="2" ref={nameRef}>
            {agent.name}
          </Text>
        </Tooltip>
      </Flex>

      {/* Description */}
      <Text fontSize="sm" color="fg.tertiary" lineClamp="3">
        {agent.description || 'No description available'}
      </Text>

      {/* Tags */}
      <Flex flexDirection="row" gap="8" flexWrap="wrap">
        <TagDisplay tags={agent.tags} quantity={2} />
      </Flex>
    </Flex>
  );
}
